import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

const Pagination = ({
  totalCount,
  pageSize,
  currentPage,
  onPageChange,
  selectedCount = 0,
}) => {
  const totalPages = Math.ceil(totalCount / pageSize);

  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;

    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = startPage + maxVisiblePages - 1;

    if (endPage > totalPages) {
      endPage = totalPages;
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return pages;
  };

  if (totalCount === 0) return null;

  // Shared button styling — kept in a constant to stay DRY across the controls
  const btnBase =
    "shrink-0 inline-flex items-center justify-center gap-2 h-9 min-w-[2.25rem] px-3 text-[13px] font-medium rounded-lg border transition-all duration-200 " +
    "disabled:opacity-45 disabled:pointer-events-none";
  const btnIdle =
    "border-border bg-surface text-text_secondary shadow-sm hover:bg-surface_alt hover:border-border_strong hover:text-text_primary hover:-translate-y-0.5 active:translate-y-0";

  return (
    <div
      className="flex items-center gap-3 rounded-xl border border-border bg-surface px-3 py-3 overflow-x-auto sm:flex-wrap sm:justify-between sm:overflow-visible sm:px-5"
      style={{ scrollbarWidth: "none", WebkitOverflowScrolling: "touch" }}
    >
      {/* Info pill */}
      <div className="shrink-0 whitespace-nowrap rounded-full border border-border bg-surface_alt px-4 py-1.5 text-[12px] font-medium leading-5 text-text_secondary sm:whitespace-normal sm:text-[12.5px]">
        <span className="text-text_primary">Page {currentPage}</span> of {totalPages}
        <span className="mx-1.5 text-text_muted">•</span>
        Showing {(currentPage - 1) * pageSize + 1}–
        {Math.min(currentPage * pageSize, totalCount)} of {totalCount}
        {selectedCount > 0 && (
          <span className="text-primary"> • {selectedCount} selected</span>
        )}
      </div>

      {/* Controls */}
      <div className="flex shrink-0 items-center justify-center gap-1.5">
        <button
          type="button"
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className={`group ${btnBase} ${btnIdle} px-3.5`}
        >
          <FontAwesomeIcon
            icon={faChevronLeft}
            className="text-[11px] transition-transform duration-200 group-hover:-translate-x-0.5"
          />
          <span className="hidden sm:inline">Previous</span>
        </button>

        {getPageNumbers().map((page) => {
          const isActive = currentPage === page;
          return (
            <button
              key={page}
              type="button"
              onClick={() => onPageChange(page)}
              aria-current={isActive ? "page" : undefined}
              className={
                isActive
                  ? `${btnBase} border-transparent bg-gradient-to-r from-secondary to-primary text-white shadow-md shadow-primary/20`
                  : `${btnBase} ${btnIdle}`
              }
            >
              {page}
            </button>
          );
        })}

        <button
          type="button"
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className={`group ${btnBase} ${btnIdle} px-3.5`}
        >
          <span className="hidden sm:inline">Next</span>
          <FontAwesomeIcon
            icon={faChevronRight}
            className="text-[11px] transition-transform duration-200 group-hover:translate-x-0.5"
          />
        </button>
      </div>

      {/* Page size */}
      <select
        value={pageSize}
        onChange={(e) => onPageChange(1, parseInt(e.target.value))}
        className="h-9 shrink-0 cursor-pointer rounded-lg border border-border bg-surface px-3 text-[12.5px] font-medium text-text_primary shadow-sm transition-colors duration-200 hover:border-border_strong focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
      >
        {[5, 10, 25, 50, 100].map((size) => (
          <option key={size} value={size} className="bg-surface text-text_primary">
            {size} per page
          </option>
        ))}
      </select>

      <style jsx>{`
        .overflow-x-auto::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default Pagination;
